import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Check, ScanLine, ShoppingCart, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { findVehicle, CATEGORY_LABELS } from "@/lib/vehicles";
import { useVehicleState } from "@/lib/vehicleStorage";
import { VehicleSelector } from "@/components/VehicleSelector";
import { Stars } from "@/components/Stars";
import { Reveal } from "@/components/Reveal";
import { MaskedLines } from "@/components/MaskedLines";
import { Seo } from "@/components/Seo";
import { useLang } from "@/i18n";
import { fmtPrice } from "@/lib/format";

const CATEGORY_IMAGES = {
  suv: "/images/glacier-suv.png",
  berline: "/images/fjord-berline.png",
  camion: "/images/toundra-camion.png",
  compact: "/images/banquise-compacte.png",
};

export default function Products() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [vehicle, setVehicle] = useVehicleState();

  useEffect(() => {
    api.get("/products").then((r) => setProduct(r.data[0] || null)).catch(() => {});
  }, []);

  const chosen = findVehicle(vehicle.marque, vehicle.modele);
  const showcaseImg = chosen ? CATEGORY_IMAGES[chosen.category] : "/images/sieges-avant.png";
  const vehicleLabel = chosen
    ? `${vehicle.marque} ${vehicle.modele}${vehicle.annee ? ` ${vehicle.annee}` : ""}`
    : null;

  const goCheckout = () => {
    const q = chosen ? `?marque=${vehicle.marque}&modele=${vehicle.modele}&annee=${vehicle.annee}` : "";
    navigate(`/commander${q}`);
  };

  return (
    <main className="mx-auto max-w-7xl px-4 pt-32 pb-24 sm:px-8 lg:px-16">
      <Seo title={`${t("products.l1")} ${t("products.l2")} | NordiGuard`} description={t("products.sub")} />

      <header className="pt-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("products.eyebrow")}</p>
        <h1 className="font-display mt-3 text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
          <MaskedLines lines={[t("products.l1"), t("products.l2")]} />
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">{t("products.sub")}</p>
      </header>

      {/* Sélecteur véhicule */}
      <Reveal className="mt-10">
        <div data-testid="catalog-vehicle-selector" className="glacial-card p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-sky-500/25 bg-sky-500/10 text-sky-400">
              <ScanLine size={19} />
            </span>
            <div>
              <h2 className="font-display text-base font-bold text-slate-50 sm:text-lg">{t("products.vehTitle")}</h2>
              <p className="text-xs text-slate-500">{t("products.vehSub")}</p>
            </div>
          </div>
          <div className="mt-5">
            <VehicleSelector value={vehicle} onChange={setVehicle} />
          </div>
          <AnimatePresence>
            {chosen && (
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                data-testid="catalog-vehicle-result"
                className="mt-4 flex items-start gap-2.5 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-sm text-emerald-200"
              >
                <BadgeCheck size={17} className="mt-0.5 shrink-0 text-emerald-400" />
                {t("products.bannerOk", { vehicle: vehicleLabel, category: CATEGORY_LABELS[chosen.category] })}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      </Reveal>

      {/* Vitrine produit unique */}
      {product && (
        <Reveal className="mt-10">
          <div data-testid="product-showcase" className="glacial-card grid overflow-hidden lg:grid-cols-2">
            <Link to={`/produit/${product.id}`} data-testid="showcase-image-link" className="relative block overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.img
                  key={showcaseImg}
                  src={showcaseImg}
                  alt="Tapis NordiGuard Sur-Mesure 3D"
                  initial={{ opacity: 0, scale: 1.04 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full w-full object-cover"
                />
              </AnimatePresence>
              <span className="absolute top-4 left-4 rounded-full bg-sky-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-950">
                {lang === "en" ? product.badge_en || product.badge : product.badge}
              </span>
              {chosen && (
                <span className="absolute top-4 right-4 rounded-full bg-emerald-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.15em] text-slate-950 shadow-[0_0_18px_rgba(52,211,153,0.5)]">
                  {t("products.yourVehicle")}
                </span>
              )}
            </Link>

            <div className="flex flex-col justify-center gap-4 p-8 lg:p-12">
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
                  {lang === "en" ? product.category_label_en || product.category_label : product.category_label}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-slate-400">
                  <Stars value={product.rating} size={12} /> {product.rating.toFixed(1)} · {product.reviews} {t("products.reviewsSuffix")}
                </span>
              </div>
              <h2 className="font-display text-2xl font-extrabold text-slate-50 sm:text-3xl">NordiGuard {product.name}</h2>
              <p className="inline-flex w-fit items-center gap-2 rounded-full border border-sky-400/30 bg-sky-500/10 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-sky-300 sm:text-xs">
                <Sparkles size={13} className="shrink-0 text-sky-400" />
                {t("marketing.tagline")}
              </p>
              <p className="text-sm leading-relaxed text-slate-400 sm:text-base">
                {lang === "en" ? product.short_en || product.short : product.short}
              </p>
              <ul className="space-y-2.5">
                {(lang === "en" ? product.features_en || product.features : product.features).slice(0, 4).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-500/15 text-sky-400">
                      <Check size={12} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-display text-3xl font-extrabold text-slate-50">{fmtPrice(product.price, lang)}</span>
                <span className="text-xs text-slate-500">{t("products.perSet")}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                <button
                  data-testid="showcase-order-button"
                  onClick={goCheckout}
                  className="group inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-slate-950 transition-all duration-300 hover:bg-sky-300 hover:shadow-[0_0_26px_rgba(56,189,248,0.45)]"
                >
                  <ShoppingCart size={16} />
                  {t("products.order")}
                </button>
                <Link
                  to={`/produit/${product.id}`}
                  data-testid="showcase-details-link"
                  className="group inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-sky-400/60 hover:text-sky-300"
                >
                  {t("products.details")}
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      )}

      {/* Fabrication sur mesure */}
      <Reveal className="mt-16">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div className="glacial-card overflow-hidden">
            <img
              src="/images/fabrication-sur-mesure.png"
              alt="Vue éclatée du tapis NordiGuard"
              loading="lazy"
              className="w-full object-cover"
              data-testid="fabrication-image"
            />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-400">{t("products.fabEyebrow")}</p>
            <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">{t("products.fabTitle")}</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-400 sm:text-base">{t("products.fabText")}</p>
            <Link
              to="/a-propos"
              data-testid="fabrication-about-link"
              className="group mt-6 inline-flex items-center gap-2 text-sm font-semibold text-sky-400 transition-colors hover:text-sky-300"
            >
              {t("products.fabLink")}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </Reveal>
    </main>
  );
}
